/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { AriaAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';
import { useRovingTabIndexContainer } from '../hooks/PRIVATE_useRovingTabIndexContainer';
import { TabbableModeContext, useTabbableMode } from '../hooks/UNSAFE_useTabbableMode';
import { ButtonLayout } from '#UNSAFE_ButtonLayout';

type AriaProps = Pick<AriaAttributesSignalExcluded, 'aria-controls' | 'aria-label'>;

type ToolbarProps = AriaProps & {
  /**
   * Specifies the children.
   */
  children: ComponentChildren;

  /**
   * Specifies the spacing between content.
   * 'sm' spacing is recommended for button variants that don't have a background or border, for example borderless buttons
   * 'lg' spacing is recommended for button variants that have a background or border, for example outlined or solid buttons
   */
  spacing?: 'sm' | 'lg';
};
/**
 * A toolbar groups a set of controls, such as buttons and menu buttons, into a single tab stop.
 */
export const Toolbar = ({
  'aria-controls': ariaControls,
  'aria-label': ariaLabel,
  children,
  spacing = 'lg'
}: ToolbarProps) => {
  const { isTabbable } = useTabbableMode();
  const { focusedId, rovingTabIndexContainerProps } = useRovingTabIndexContainer(!isTabbable);

  return (
    <div
      aria-controls={ariaControls}
      aria-label={ariaLabel}
      role="toolbar"
      {...rovingTabIndexContainerProps}>
      <ButtonLayout spacing={spacing}>
        <TabbableModeContext.Provider
          value={{ isTabbable: isTabbable, isRoving: true, focusedId: focusedId }}>
          {children}
        </TabbableModeContext.Provider>
      </ButtonLayout>
    </div>
  );
};
